import { useState, useRef, useEffect } from 'react'
import { SupportMessageSenderType } from '@/gen/cortado/v1/support_pb'
import type { SupportMessage } from '@/gen/cortado/v1/support_pb'
import { useSupportThread, useSupportArchive, useSupportSendMessage } from '@/stores/support.store'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, Send, Archive, Loader2 } from 'lucide-react'
import { StatusBadge } from './StatusBadge'
import { formatTimeAgo } from './utils'
import { cn } from '@/lib/utils'

interface ThreadDetailProps {
  threadId: string
  onBack: () => void
}

export function ThreadDetail({ threadId, onBack }: ThreadDetailProps) {
  const [messageBody, setMessageBody] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  const { thread, messages, hasLoaded, isArchived } = useSupportThread(threadId)
  const { archive, isPending: isArchivePending, archivingThreadId } = useSupportArchive()
  const { send, isPending: isSendPending } = useSupportSendMessage(threadId)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages.length])

  function handleSend() {
    const body = messageBody.trim()
    if (!body || isSendPending) return
    send(body)
    setMessageBody('')
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  async function handleArchive() {
    await archive(threadId)
    onBack()
  }

  const isArchiving = isArchivePending && archivingThreadId === threadId

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 px-4 pb-3 border-b">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-7 w-7" aria-label="Back to threads">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium" title={thread?.subject}>{thread?.subject || 'Thread'}</p>
            {isArchived && <Badge variant="outline">Archived</Badge>}
          </div>
        </div>
        {thread && <StatusBadge status={thread.status} />}
        {thread && !isArchived && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleArchive}
            disabled={isArchiving}
            className="h-7 w-7"
            aria-label="Archive thread"
            title="Archive thread"
          >
            {isArchiving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
          </Button>
        )}
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-3 p-4">
          {!hasLoaded ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col items-end gap-1">
                <Skeleton className="h-10 w-3/4 rounded-lg" />
                <Skeleton className="h-3 w-20" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <Skeleton className="h-10 w-2/3 rounded-lg" />
                <Skeleton className="h-3 w-20" />
              </div>
              <div className="flex flex-col items-end gap-1">
                <Skeleton className="h-10 w-1/2 rounded-lg" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              No messages yet
            </div>
          ) : (
            messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)
          )}
        </div>
      </div>

      <div className="border-t p-3">
        {isArchived ? (
          <p className="text-center text-xs text-muted-foreground py-1">This thread is archived</p>
        ) : (
          <div className="flex gap-2">
            <Textarea
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              aria-label="Message"
              className="min-h-[36px] max-h-[100px] resize-none text-sm"
              rows={1}
            />
            <Button
              size="icon"
              onClick={handleSend}
              disabled={!messageBody.trim() || isSendPending}
              className="h-9 w-9 shrink-0"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function MessageBubble({ message }: { message: SupportMessage }) {
  const isUser = message.senderType === SupportMessageSenderType.USER

  return (
    <div className={cn('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-foreground',
        )}
      >
        {message.body}
      </div>
      <div className="flex items-center gap-1 px-1 text-xs text-muted-foreground">
        <span>{message.senderName}</span>
        {message.createdAt && <span>· {formatTimeAgo(message.createdAt)}</span>}
      </div>
    </div>
  )
}
